import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

// Eagerly load German only — it is the fallback and must always be present.
// Every other locale is lazy-loaded on demand (see loadLocale below), so the
// initial bundle stays small even with 21 supported languages.
import de from './locales/de.json'

// Cherry's supported languages. German is the source of truth and the fallback;
// English is the first translation. More languages are added by dropping in a
// new locale JSON and registering it here (and in localeLoaders below).
export const SUPPORTED_LANGUAGES = ['de', 'en', 'zh', 'hi', 'es', 'fr', 'pt', 'bn', 'ru', 'id', 'ja', 'nl', 'da', 'tr', 'pl', 'sv', 'cs', 'ro', 'el', 'ar', 'he'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  de: 'Deutsch',
  en: 'English',
  zh: '中文',
  hi: 'हिन्दी',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
  bn: 'বাংলা',
  ru: 'Русский',
  id: 'Bahasa Indonesia',
  ja: '日本語',
  nl: 'Nederlands',
  da: 'Dansk',
  tr: 'Türkçe',
  pl: 'Polski',
  sv: 'Svenska',
  cs: 'Čeština',
  ro: 'Română',
  el: 'Ελληνικά',
  ar: 'العربية',
  he: 'עברית',
}

// Lazy locale loaders. Vite needs statically analyzable import paths, so each
// locale gets an explicit entry instead of a dynamic `import('./locales/' + lng)`.
// German is intentionally absent — it is eagerly imported above as the fallback.
type JsonModule = { default: Record<string, unknown> } | Record<string, unknown>

const localeLoaders: Partial<Record<SupportedLanguage, () => Promise<JsonModule>>> = {
  en: () => import('./locales/en.json'),
  zh: () => import('./locales/zh.json'),
  hi: () => import('./locales/hi.json'),
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
  pt: () => import('./locales/pt.json'),
  bn: () => import('./locales/bn.json'),
  ru: () => import('./locales/ru.json'),
  id: () => import('./locales/id.json'),
  ja: () => import('./locales/ja.json'),
  nl: () => import('./locales/nl.json'),
  da: () => import('./locales/da.json'),
  tr: () => import('./locales/tr.json'),
  pl: () => import('./locales/pl.json'),
  sv: () => import('./locales/sv.json'),
  cs: () => import('./locales/cs.json'),
  ro: () => import('./locales/ro.json'),
  el: () => import('./locales/el.json'),
  ar: () => import('./locales/ar.json'),
  he: () => import('./locales/he.json'),
}

// Tracks which locales are already registered, so each JSON chunk is fetched
// and added at most once per session.
const loadedLocales = new Set<string>(['de'])

async function loadLocale(lng: string): Promise<void> {
  // Match i18next's `load: 'languageOnly'`: only ever deal with base codes.
  const base = lng.split('-')[0]
  if (loadedLocales.has(base)) return
  const loader = localeLoaders[base as SupportedLanguage]
  if (!loader) return
  try {
    const data = await loader()
    const translations =
      'default' in (data as JsonModule)
        ? (data as { default: Record<string, unknown> }).default
        : (data as Record<string, unknown>)
    // deep + overwrite make this idempotent even if a race loaded it twice.
    i18n.addResourceBundle(base, 'translation', translations, true, true)
    loadedLocales.add(base)
    // addResourceBundle alone does NOT notify react-i18next's bound components
    // (i18next doesn't emit 'added' for it), so re-assert the language: this
    // emits 'languageChanged', which react-i18next listens to, and every
    // useTranslation() hook re-renders with the freshly loaded bundle.
    if (i18n.language.split('-')[0] === base) {
      void i18n.changeLanguage(base)
    }
  } catch (err) {
    // Network/chunk failure: silently fall back to German rather than crash.
    console.warn(`[i18n] Failed to load locale '${base}', using fallback.`, err)
  }
}

// ── RTL / document metadata ──
// Keeps <html dir> and <html lang> in sync with the active language so
// Arabic and Hebrew render right-to-left and assistive tech gets the right
// language. Runs on init and on every language change.
const RTL_LANGUAGES = new Set<string>(['ar', 'he'])

function applyDocumentDirection(lng: string): void {
  if (typeof document === 'undefined') return
  const base = lng.split('-')[0]
  document.documentElement.dir = RTL_LANGUAGES.has(base) ? 'rtl' : 'ltr'
  document.documentElement.lang = base
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      de: { translation: de },
    },
    fallbackLng: 'de',
    supportedLngs: SUPPORTED_LANGUAGES,
    // Only match the base language ('en' from 'en-US'), so 'en-GB', 'en-US' etc.
    // all resolve to our single 'en' bundle.
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'cherry_lang',
      caches: ['localStorage'],
    },
    interpolation: {
      // React already escapes values, so i18next must not double-escape.
      escapeValue: false,
    },
  })
  .then(() => {
    // Lazy-load the initially detected language (if it isn't German) and set
    // the document direction once startup has settled.
    void loadLocale(i18n.language)
    applyDocumentDirection(i18n.language)
  })

// Lazy-load and re-apply document direction whenever the language changes.
i18n.on('languageChanged', (lng) => {
  void loadLocale(lng)
  applyDocumentDirection(lng)
})

export default i18n
